import { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import { useGetVideosByApiQuery } from "../../services/modulesuser/videoListApi/index.js";
import { setVideosByKey } from "../../store/video";
import { titleToKey } from "../../services/utils/helpers.js";
import { setWatchlist, setFavorites } from "../../store/user";

import Loader from "../../component/ui/Loader.js";
import Heading from "../../component/ui/Heading.js";
import ButtonsPreview from "../../component/previews/ButtonsPreview.js";
import PreviewImage from "../../component/previews/PreviewImage.js";

import useSwipeScroll from "../../hooks/useSwipeScroll.js";

function VideoRow({ title, api }) {
  const dispatch = useDispatch();
  const rowRef = useRef(null);

  // Abilita swipe / drag to scroll
  useSwipeScroll(rowRef, { friction: 1, enableWheelX: true });

  const { data, isLoading, error } = useGetVideosByApiQuery(api);

  const videos = useMemo(() => {
    const result = data?.data?.result;
    if (!result) return [];

    // Case 1: /user/recommended e /user/top5
    if (Array.isArray(result)) {
      return result.map((entry) => ({
        id: entry.video.id,
        title: entry.video.title,
        thumbnail: entry.video.thumbnail,
        rating: entry.video.rating || 8,
        url: entry.video.url,
        description: entry.video.description,
        createdAt: entry.video.createdAt,
        categories: Array.isArray(entry.video.categories) ? entry.video.categories : [],
      }));
    }

    // Case 2: /user/getlatestvideosbynetwork
    if (Array.isArray(result?.videos)) {
      return result.videos.map((video) => ({
        id: video.id,
        title: video.title,
        thumbnail: video.thumbnail,
        rating: video.rating || 0,
        url: video.url,
        description: video.description,
        createdAt: video.createdAt,
        categories: Array.isArray(video.categories) ? video.categories : [],
      }));
    }

    return [];
  }, [data]);

  useEffect(() => {
    if (!videos.length) return;

    // aggiorna liste utente se la row rappresenta watchlist o favorites
    if (api === "/views/unfinished") {
      dispatch(setWatchlist(videos));
    } else if (api === "/favorites/getall") {
      dispatch(setFavorites(videos));
    }

    // aggiorna cache globale per chiave derivata dal titolo
    dispatch(
      setVideosByKey({
        key: titleToKey(title),
        videos,
        title,
      })
    );
  }, [dispatch, api, title, videos]);

  if (isLoading) return <Loader />;
  if (error || !videos.length) return null;

  return (
    <section className="mt-8">
      <Heading>{title}</Heading>
      <div className="relative">
        <ButtonsPreview
          ribbon={rowRef}
          length={videos.length}
          isScrollByOne={true}
        />
        <div
          data-xscroll
          ref={rowRef}
          className="
            flex overflow-x-auto scroll-smooth scrollbar-hide mt-3
            gap-[100px] items-center cursor-grab select-none
          "
          style={{
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-x",
            overscrollBehaviorX: "contain",
          }}
        >
          <div className="grid grid-flow-col auto-cols-[320px] gap-[15px]">

            {videos.map((video) => (
              <div key={video.id} className="relative flex items-start">
                <div
                  style={{ flexBasis: 320 }}
                  className="block flex-shrink-0"
                >
                  <Link
                    to={`/videos/${video.id}`}
                    state={{ video }}
                    className="block w-full"
                  >
                    <PreviewImage media={video} type="tv" hud={true} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default VideoRow;
